/**
 * Review queue API (owner: C1): GET /api/kb/review/…, GET /api/kb/review/reasons and
 * POST /api/kb/review/{id}/decide (docs/mvp-build-spec.md §5). Query keys start with "review".
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/api/client";
import type { IsoDateTime, Paged, Uuid } from "@/api/types";
import type { SubstanceListItem } from "@/features/materials/api";

export type ReviewStatus = "open" | "accepted" | "rejected" | "deferred";
export type ReviewDecision = "accepted" | "rejected" | "deferred";
/** What accepting applies (backend kb.services.REVIEW_ACCEPT_ACTIONS); null records the decision only. */
export type AcceptAction = "link" | "confirm_substance" | "choose_cas" | "set_cas" | "set_odor_path";

export interface ReviewItem {
  id: Uuid;
  reason_code: string;
  reason_label: string;
  summary: string;
  entity_type: string;
  entity_id: string;
  entity_label: string;
  material_id: Uuid | null;
  priority: number;
  status: ReviewStatus;
  created_at: IsoDateTime;
  decided_at: IsoDateTime | null;
  note: string;
}

export interface ReviewEntity {
  kind?: "material" | "substance" | "supplier_product" | "source_record";
  id?: Uuid;
  name?: string;
  title?: string;
  supplier?: string;
  material_kind?: string;
  preferred_name?: string;
  cas?: string | null;
  cas_numbers?: string[];
  url?: string;
  source?: string;
  external_id?: string;
  substance?: SubstanceListItem | null;
  merged_into?: { id: Uuid } | null;
  [key: string]: unknown;
}

export interface ReviewItemDetail extends ReviewItem {
  description: string;
  evidence: Record<string, unknown>;
  proposed_action: Record<string, unknown>;
  accept_action: AcceptAction | null;
  entity: ReviewEntity;
  suggestions: SubstanceListItem[];
  cas_options: string[];
  decided_by: string | null;
}

export interface ReviewReasonCount {
  reason_code: string;
  label: string;
  count: number;
}

export interface ReviewListParams {
  status?: ReviewStatus;
  reason_code?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface DecisionParams {
  substance_id?: Uuid;
  material_id?: Uuid;
  cas?: string;
  term_ids?: Uuid[];
}

export interface DecisionInput {
  id: Uuid;
  decision: ReviewDecision;
  note?: string;
  params?: DecisionParams;
}

export const reviewKeys = {
  all: ["review"] as const,
  list: (params: ReviewListParams) => ["review", "list", params] as const,
  reasons: (status: ReviewStatus) => ["review", "reasons", status] as const,
  detail: (id: Uuid) => ["review", "detail", id] as const,
};

export function useReviewItems(params: ReviewListParams) {
  return useQuery({
    queryKey: reviewKeys.list(params),
    queryFn: ({ signal }) => apiGet<Paged<ReviewItem>>("/kb/review/", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useReviewReasons(status: ReviewStatus) {
  return useQuery({
    queryKey: reviewKeys.reasons(status),
    queryFn: ({ signal }) => apiGet<ReviewReasonCount[]>("/kb/review/reasons", { params: { status }, signal }),
  });
}

export function useReviewItem(id: Uuid | null) {
  return useQuery({
    queryKey: reviewKeys.detail(id ?? ""),
    queryFn: ({ signal }) => apiGet<ReviewItemDetail>(`/kb/review/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useDecideReviewItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note, params }: DecisionInput) =>
      apiPost<ReviewItemDetail>(`/kb/review/${id}/decide`, { decision, note: note ?? "", params: params ?? {} }),
    onSuccess: (detail) => queryClient.setQueryData(reviewKeys.detail(detail.id), detail),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["review", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["review", "reasons"] });
      await queryClient.invalidateQueries({ queryKey: ["materials"] });
    },
  });
}
